import React from 'react';
import { api, extractErr } from '@/lib/api';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useLive, useLiveEvent, MuteToggle, DesktopAlertToggle, openInboxChat } from '@/components/LiveEvents';
import { toMytRelative } from '@/lib/date';
import { Bell, BellOff, ChevronDown } from 'lucide-react';

export const STATUS = {
  available: { label: 'Available', dot: 'bg-[#34C759]' },
  away: { label: 'Away', dot: 'bg-[#FF9500]' },
  off: { label: 'Off shift', dot: 'bg-slate-400' },
};

export const StatusDot = ({ status, className = '' }) => <span className={`inline-block h-2 w-2 rounded-full ${(STATUS[status] || STATUS.available).dot} ${className}`} />;

export function StatusMenu() {
  const [st, setSt] = React.useState(null);
  React.useEffect(() => { api.get('/team/members').then(({ data }) => setSt({ status: data.my_status, onShift: data.my_on_shift, shift: data.my_shift })).catch(() => {}); }, []);
  const set = async (status) => {
    try { await api.put('/me/status', { status }); setSt((s) => ({ ...s, status })); toast.success(`Status: ${STATUS[status].label}`); }
    catch (e) { toast.error(extractErr(e, 'Could not update status.')); }
  };
  if (!st) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="flex items-center gap-1.5 text-[11px] text-slate-500 hover:text-[#1D1D1F] transition-colors duration-150" data-testid="status-menu-trigger">
        <StatusDot status={st.status} />{STATUS[st.status]?.label}{st.shift?.days?.length > 0 && !st.onShift && ' · outside shift'}<ChevronDown className="h-3 w-3" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {Object.entries(STATUS).map(([k, v]) => (
          <DropdownMenuItem key={k} onClick={() => set(k)} data-testid={`status-option-${k}`}><StatusDot status={k} className="mr-2" />{v.label}</DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export const openLead = (id) => {
  if (window.location.pathname.startsWith('/vc/leads')) window.dispatchEvent(new CustomEvent('verse:open-lead', { detail: id }));
  else { window.history.pushState(null, '', `/vc/leads?open=${id}`); window.dispatchEvent(new PopStateEvent('popstate')); }
};

// Chats that popped up on this device (my chats + unassigned), newest first.
const RecentChats = ({ items, onOpen }) => (items.length === 0
  ? <div className="px-4 py-6 text-sm text-slate-500 text-center" data-testid="notif-chats-empty">No new chats yet</div>
  : items.map((c) => (
    <button key={c.customer_id} onClick={() => onOpen(c)} data-testid={`notif-chat-${c.customer_id}`}
      className={`w-full text-left px-4 py-3 border-b border-black/5 last:border-0 transition-colors duration-150 hover:bg-black/[0.03] ${c.read ? '' : 'bg-[#C7A46A]/10'}`}>
      <div className="flex items-center gap-1.5 text-xs text-slate-500">
        <span className="font-medium text-[#1D1D1F] truncate">{c.lead ? `New lead · ${c.title}` : c.title}</span>
        {!c.read && c.count > 1 && <span className="tabular-nums">· {c.count} new</span>}
        <span className="ml-auto shrink-0">{c.channel ? `${c.channel} · ` : ''}{toMytRelative(c.at)}</span>
      </div>
      <div className="text-sm text-slate-700 line-clamp-2 mt-0.5">{c.text}</div>
    </button>
  )));

export function NotificationBell() {
  const [data, setData] = React.useState({ items: [], unread: 0, unread_action: 0, unread_system: 0 });
  const [tab, setTab] = React.useState('action');
  const load = React.useCallback(() => api.get('/notifications').then(({ data: d }) => setData(d)).catch(() => {}), []);
  React.useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  useLiveEvent((ev) => { if (ev.kind === 'notification') load(); });
  const [show, setShow] = React.useState(false);
  const open = async (n) => {
    setShow(false);
    window.dispatchEvent(new Event('verse:close-nav'));
    if (!n.read) await api.post(`/notifications/${n.id}/read`).catch(() => {});
    load();
    if (['ads_alert', 'security', 'qualify_nudge', 'system_alert'].includes(n.kind)) window.location.assign(n.link || '/vc/ads'); else openLead(n.customer_id);
  };
  const readAll = async () => { await api.post('/notifications/read-all').catch(() => {}); load(); };
  const { status, muted, recent, setRecent } = useLive();
  const chatsUnread = recent.filter((c) => !c.read).length;
  const openChat = (c) => {
    setShow(false);
    window.dispatchEvent(new Event('verse:close-nav'));
    setRecent((r) => r.map((x) => (x.customer_id === c.customer_id ? { ...x, read: true } : x)));
    openInboxChat(c.customer_id);
  };
  const badge = data.unread_action + chatsUnread;
  const BellIcon = muted ? BellOff : Bell;
  return (
    <Popover modal open={show} onOpenChange={(o) => { setShow(o); if (o) setTab(chatsUnread ? 'chats' : !data.unread_action && data.unread_system ? 'system' : 'action'); }}>
      <PopoverTrigger className="relative h-8 w-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-black/5 hover:text-[#1D1D1F] transition-colors duration-150" data-testid="notif-bell" data-muted={muted ? 'true' : 'false'} title={muted ? 'Notifications · sound muted' : 'Notifications'}>
        <BellIcon className="h-4 w-4" />
        {badge > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#FF3B30] text-white text-[10px] leading-4 text-center tabular-nums" data-testid="notif-unread-count">{badge > 99 ? '99+' : badge}</span>}
        {!badge && data.unread_system > 0 && <span className="absolute top-0.5 right-0.5 h-2 w-2 rounded-full bg-amber-500" data-testid="notif-system-dot" />}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0 rounded-xl" data-testid="notif-panel">
        {status !== 'live' && <div className="px-4 py-1.5 text-[11px] text-amber-700 bg-amber-50 border-b border-amber-100 rounded-t-xl" data-testid="notif-reconnecting">{status === 'connecting' ? 'Connecting…' : 'Reconnecting…'}</div>}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-black/5">
          <span className="text-sm font-medium">Notifications</span>
          {(data.unread_action + data.unread_system + chatsUnread) > 0 && <button onClick={() => { setRecent((r) => r.map((x) => ({ ...x, read: true }))); readAll(); }} className="text-xs text-[#A8864F] hover:underline" data-testid="notif-read-all">Mark all read</button>}
        </div>
        <div className="flex gap-1 px-3 pt-2" data-testid="notif-tabs">
          {[['chats', 'Chats', chatsUnread], ['action', 'Needs action', data.unread_action], ['system', 'Ads & system', data.unread_system]].map(([k, l, c]) => (
            <button key={k} type="button" onClick={() => setTab(k)} data-testid={`notif-tab-${k}`}
              className={`rounded-full px-3 py-1 text-xs transition-colors duration-150 ${tab === k ? 'bg-[#1D1D1F] text-white' : 'text-slate-600 hover:bg-black/5'}`}>
              {l}{c > 0 && <span className="ml-1 tabular-nums" data-testid={`notif-tab-${k}-count`}>{c}</span>}
            </button>))}
        </div>
        <div className="max-h-80 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch] touch-pan-y" data-testid="notif-list">
          {tab === 'chats' && <RecentChats items={recent} onOpen={openChat} />}
          {tab !== 'chats' && data.items.filter((n) => n.group === tab).length === 0 && <div className="px-4 py-6 text-sm text-slate-500 text-center" data-testid="notif-empty">{tab === 'action' ? 'Nothing needs you right now' : 'No ads or system alerts'}</div>}
          {tab !== 'chats' && data.items.filter((n) => n.group === tab).map((n) => (
            <button key={n.id} onClick={() => open(n)} data-testid={`notif-item-${n.id}`}
              className={`w-full text-left px-4 py-3 border-b border-black/5 last:border-0 transition-colors duration-150 hover:bg-black/[0.03] ${n.read ? '' : 'bg-[#C7A46A]/10'}`}>
              {n.resolved && ['fixed', 'replied', 'qualified'].includes(n.resolved) && <div className="text-[10px] uppercase tracking-wide text-emerald-700" data-testid={`notif-resolved-${n.id}`}>{n.resolved === 'replied' ? 'Handled' : 'Fixed'}</div>}
              {n.kind === 'system_alert'
                ? <div className={`text-xs font-medium ${n.level === 'critical' ? 'text-red-600' : 'text-amber-700'}`}>System · {n.title} · {toMytRelative(n.created_at)}</div>
                : n.kind === 'ads_alert'
                ? <div className={`text-xs font-medium ${n.level === 'critical' ? 'text-red-600' : 'text-amber-700'}`}>Ads alert · {n.title} · {toMytRelative(n.created_at)}</div>
                : n.kind === 'qualify_nudge'
                  ? <div className="text-xs font-medium text-amber-700">To qualify · {n.title} · {toMytRelative(n.created_at)}</div>
                : n.kind === 'security'
                  ? <div className="text-xs font-medium text-red-600">Security · {toMytRelative(n.created_at)}</div>
                  : n.kind === 'visit_declined'
                  ? <div className="text-xs font-medium text-red-600">Can't make it · {n.customer_name || 'a lead'} · {toMytRelative(n.created_at)}</div>
                  : n.kind === 'booking_spotted'
                  ? <div className="text-xs font-medium text-indigo-700">Booking spotted · {n.customer_name || 'a lead'} · {toMytRelative(n.created_at)}</div>
                  : n.kind === 'escalation'
                  ? <div className="text-xs font-medium text-red-600">Not contacted · {n.customer_name || 'a lead'} · {toMytRelative(n.created_at)}</div>
                  : n.kind === 'assign_fallback'
                    ? <div className="text-xs text-slate-500"><span className="font-medium text-[#1D1D1F]">Auto-assign</span> · {n.customer_name || 'a lead'} · {toMytRelative(n.created_at)}</div>
                    : <div className="text-xs text-slate-500"><span className="font-medium text-[#1D1D1F]">{n.from_name}</span> mentioned you on <span className="font-medium text-[#1D1D1F]">{n.customer_name || 'a lead'}</span> · {toMytRelative(n.created_at)}</div>}
              <div className="text-sm text-slate-700 line-clamp-2 mt-0.5">{n.body}</div>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 px-4 py-2.5 border-t border-black/5" data-testid="notif-alert-settings"><MuteToggle /><DesktopAlertToggle /></div>
      </PopoverContent>
    </Popover>
  );
}
